import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Col, Modal, Row } from 'antd';

import { DialogModalTypes } from 'constants/enums';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import styles from './DialogModal.module.scss';

interface DialogModalProps {
  isOpen: boolean;
  type: DialogModalTypes;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  title?: string;
  description?: string | ReactElement;
  confirmLabel?: string;
  confirmLoading?: boolean;
}

const MODAL_WIDTH = '550px';

const DialogModal: FC<DialogModalProps> = ({
  isOpen,
  type,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmLoading = false
}) => {
  const { t } = useTranslation();

  const deleteButton = (
    <Button
      type="primary"
      className={styles.deleteButton}
      onClick={onConfirm}
      icon={<TrashIcon className="icon-trash" />}
      disabled={confirmLoading}
      loading={confirmLoading}
    >
      {t('common.delete')}
    </Button>
  );

  const dangerConfirmButton = (
    <Button
      type="primary"
      danger
      className={styles.dangerConfirmButton}
      onClick={onConfirm}
      disabled={confirmLoading}
      loading={confirmLoading}
    >
      {confirmLabel ??
        t('sidebar.application.interview.awaitingResponse.cancel')}
    </Button>
  );

  const CONFIG = {
    [DialogModalTypes.Delete]: {
      title: t('common.delete'),
      button: deleteButton
    },
    [DialogModalTypes.DangerConfirm]: {
      title: title ?? '',
      button: dangerConfirmButton
    }
  };

  return (
    <Modal
      className={styles.container}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={MODAL_WIDTH}
      title={
        title ||
        (type === DialogModalTypes.DangerConfirm
          ? t('sidebar.application.interview.cancel.title')
          : CONFIG[type].title)
      }
      closeIcon={<CloseIcon />}
      destroyOnHidden
    >
      {description && (
        <Row>
          <Col span={24} className={styles.description}>
            {description}
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24}>
          <div className={styles.buttons}>
            <Button
              type="link"
              onClick={onClose}
              className={styles.button}
              disabled={confirmLoading}
            >
              {t('common.cancel')}
            </Button>
            {CONFIG[type].button}
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default DialogModal;
